'use client';
import cn from 'classnames';
import Image from 'next/image';

import { HeaderCMSContent } from '@/components/common/Header/Header';
import Link from '@/components/common/Link/Link';
import Text from '@/components/common/Text/Text';
import ArrowRight from 'public/img/icon/arrow-right.svg';
import PriveeWordmark from 'public/img/logo/reserve-logo.svg';

import './CollectionMenu.style.scss';

type CollectionMenuProps = {
  className?: string;
  /**
   * This prop removes the content from the root div if true.
   * This is for SEO purposes as not to double up on navigation
   * links, which will lower our SEO score with Big G.
   */
  showContent?: boolean;

  featuredLinks?: HeaderCMSContent['featuredLinks'];

  /** Callback to run when a 'close' action is requested */
  onCloseClick?: () => void;
};

function CollectionMenu(props: CollectionMenuProps) {
  const {
    className = '',
    showContent = true,
    onCloseClick,
    featuredLinks
  } = props;

  return (
    <div className={cn('CollectionMenu', className)}>
      {/* Don't show content if not needed */}
      {showContent && (
        <div className='CollectionMenu-container'>
          {/* Make menu-item-back sticky with scroll container */}
          <div
            className='menu-item menu-item-back shadow-bottom cursor'
            role='button'
            onClick={onCloseClick}
          >
            <Image
              className='arrow'
              // Arrow is flipped to look left via SCSS
              // Little hack to make sure we aren't loading in
              // two SVG files. Just reuse the same one
              src={ArrowRight}
              height={12}
              width={6}
              alt='View the collection'
            />
            <Text className='text text-collection'>THE COLLECTION</Text>
          </div>
          <div className='menu-item bigger-space'>
            <a
              className='Link link-m'
              href='/collection'
            >
              VIEW ALL
            </a>
          </div>
          <div className='menu-item'>
            <a
              className='Link link-m'
              href='/collection/reserve'
            >
              <Image
                src={PriveeWordmark}
                width={68}
                height={29}
                alt='Réservé'
              />
            </a>
          </div>
          <div className='menu-item'>
            <a
              className='Link link-m'
              href='/collection?collection=Handbags'
            >
              HANDBAGS
            </a>
          </div>
          <div className='menu-item'>
            <a
              className='Link link-m'
              href='/collection?collection=Designer%20Jewelry'
            >
              DESIGNER JEWELRY
            </a>
          </div>
          <div className='menu-item'>
            <a
              className='Link link-m'
              href='/collection?collection=Fine%20Jewelry'
            >
              FINE JEWELRY
            </a>
          </div>
          <div className='menu-item menu-item-title'>
            <Text className='menu-item-title-text'>TIERS</Text>
          </div>
          <div className='menu-item'>
            <a
              className='Link link-m'
              href='/collection?subscriptionLevel=Couture'
            >
              COUTURE
            </a>
          </div>
          <div className='menu-item'>
            <a
              className='Link link-m'
              href='/collection?subscriptionLevel=Classique'
            >
              CLASSIQUE
            </a>
          </div>
          <div className='menu-item'>
            <a
              className='Link link-m'
              href='/collection?subscriptionLevel=Premier'
            >
              PREMIER
            </a>
          </div>
          {featuredLinks && (
            <>
              <div className='menu-item menu-item-title'>
                <Text className='menu-item-title-text'>FEATURED</Text>
              </div>
              {featuredLinks.map((link, index) => {
                return (
                  <div
                    key={index}
                    className={cn('menu-item', {
                      'menu-item-image': link.imageUrl
                    })}
                  >
                    <Link
                      href={link.url}
                      variant='link-m'
                      uppercase
                      aria-label={link.displayText}
                    >
                      {link.imageUrl ? (
                        <Image
                          className='menu-item-image-img'
                          src={link.imageUrl}
                          alt={link.displayText}
                          height={14}
                          width={100}
                        />
                      ) : (
                        link.displayText
                      )}
                    </Link>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CollectionMenu;
